import React, { useState, useEffect, useContext, useCallback } from "react";
import cx from "classnames";
import styles from "../scss/AFPayment.module.scss";
import Icon from "../../common/components/Icon/Icon";
import { useHistory, useParams } from "react-router-dom";
import AFSecure from "../components/AFSecure";
import AFLayout from "../components/AFLayout";
import AFSkipPaymentDialog from "../components/Modals/AFSkipPaymentDialog";
import { PageType } from "../enums/PageType";
import { setCurrentPageProgress } from "../api/ApiAppForm";
import { useAFForm } from "../hooks/useAFForm";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import AFNavigation from "../components/AFNavigation";
import { IPageInfo } from "../interfaces/IAppFormData";
import * as Sentry from "@sentry/react";


interface IAFAFPaymentOptionProps {
    applicationId: string;
}

const AFPaymentOptionPage: React.FC = () => {
    const history = useHistory();
    const params = useParams<IAFAFPaymentOptionProps>();
    const applicationId = params.applicationId;
    const [option, setOption] = useState("");
    const [isOpenDialog, setIsOpenDialog] = useState(false);

    const { selectors, actions } = useContext(AppFormControllerContext);
    const {
        getCurrentPage,
        checkValidApplication
    } = useAFForm({ pageType: PageType.Payment, currentClientNumber: 0 });

    const handleSelectOption = (value): void => {
        if (value === "skip") {
            setIsOpenDialog(true);
        } else {
            setOption(value);
            handleNextPage(value);
        }
    };

    const handleNextPage = (type): void => {
        history.push(`/app-form/submit/${applicationId}/payment/${type}/option`);
    };

    const handleBack = (): void => {
        const applicantList = selectors.getApplicantList();
        const lastApplicant = applicantList.reduce((prev, current) => (prev && prev.clientNumber > current.clientNumber) ? prev : current);
        history.push(`/app-form/details/${lastApplicant.applicantToken}/declarations`);
    };

    const handleAcknowledgement = (): void => {
        setIsOpenDialog(false);
        const pageInfo: IPageInfo = {
            pageType: PageType.Consent,
            currentClientNumber: 1
        };
        setCurrentPageProgress(
            selectors.getApplicationId(),
            selectors.getQuoteGroupId(),
            pageInfo
        );
        history.push(`/app-form/submit/${applicationId}/consent/disclosure`);
    };

    const keyDownHandler = useCallback((event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            handleBack();
        }
    }, []);


    useEffect(() => {
        checkValidApplication(applicationId, selectors);
    }, [applicationId]);

    useEffect(() => {
        document.addEventListener("keydown", keyDownHandler);
        return () => {
            document.removeEventListener("keydown", keyDownHandler);
        };
    }, []);

    return (
        <AFLayout currentPage={getCurrentPage()}>
            <AFSkipPaymentDialog
                isOpen={isOpenDialog}
                acknowledge={handleAcknowledgement}
                onClose={() => setIsOpenDialog(false)}
            />
            <div className={cx(styles["af-payment"], styles["layout"])}>
                <div
                    className={cx(
                        styles["af-payment-container"],
                        "layout-flex-column--centered"
                    )}>
                    <h2 className={cx(styles["payment-title-label"])}>
                        Preferred payment option
                    </h2>

                    <div className={styles["af-payment"]}>
                        <h3
                            className={cx(
                                styles["af-payment-label"],
                                styles["text-align--left"]
                            )}>
                            <Icon
                                alt="check-icon"
                                className={styles["af-icon"]}
                                size="xl"
                                id="GreenTick"
                            />
                            Complimentary interim cover!
                        </h3>

                        <p>
                            Choose a payment option below, and fill out your details. You
                            will be notified once your policy has been accepted by the
                            insurer and you will have time to change your mind before any
                            premiums are taken. You will get complimentary interim cover
                            if you have an accident while your application is being
                            assessed.
                        </p>

                        <p>
                            T&C's and exclusions apply and are specific to each insurer. <a href="/our-service/contact-us.aspx" target="_blank">Contact us for more information.</a>
                        </p>

                        <fieldset
                            className={cx(styles["af-radio-button"])}
                            id={`payment-group`}>
                            <legend>Payment option</legend>
                            <div className={styles["radio-button"]}>
                                <input
                                    type="radio"
                                    id="payment-debit"
                                    name={`payment-group`}
                                    value={"debit"}
                                    checked={option === "debit"}
                                    onChange={() => handleSelectOption("debit")}
                                />
                                <label
                                    className={styles["radion-button--label-container"]}
                                    htmlFor="payment-debit">
                                    <p className={styles["rb--label"]}>Direct debit</p>
                                </label>
                            </div>
                            <div className={styles["radio-button"]}>
                                <input
                                    type="radio"
                                    id="payment-credit"
                                    name={`payment-group`}
                                    value={"credit"}
                                    checked={option === "credit"}
                                    onChange={() => handleSelectOption("credit")}
                                />
                                <label
                                    className={styles["radion-button--label-container"]}
                                    htmlFor="payment-credit">
                                    <p className={styles["rb--label"]}>Credit card</p>
                                </label>
                            </div>
                            <div className={styles["radio-button"]}>
                                <input
                                    type="radio"
                                    id="payment-skip"
                                    name={`payment-group`}
                                    value={"skip"}
                                    onClick={() => handleSelectOption("skip")}
                                />
                                <label
                                    className={styles["radion-button--label-container"]}
                                    htmlFor="payment-skip">
                                    <p className={styles["rb--label"]}>Organise Later</p>
                                </label>
                            </div>
                        </fieldset>
                        <AFNavigation
                            handleBack={handleBack}
                            skipIsVisible={false}
                            forwardIsVisible={false}
                            forwardIsDisabled={true}
                            forwardType="submit"
                            forwardText={""}
                        />
                    </div>
                </div>
            </div>
        </AFLayout>
    );
};

export default AFPaymentOptionPage;
